import {
  collection,
  collectionGroup,
  doc,
  documentId,
  getCountFromServer,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  startAfter,
  where as fsWhere,
} from "firebase/firestore";
import { Repository } from "src/firebase/firestore/repository";
import { db } from "src/firebase";
import { UserSubscription } from "src/types/subscription.types";
import { SubscriptionUser } from "../types/subscription-user.entity";

type SubscriptionUsersCursor = {
  userId: string;
  subscriptionId: string;
  seenUserIds: string[];
};

class SubscriptionUserRepository extends Repository<SubscriptionUser> {
  private readonly subscriptionGroupName = "user_subscriptions";

  constructor() {
    super("users");
  }

  private async mapWithConcurrency<T, R>(items: T[], concurrency: number, mapper: (item: T) => Promise<R>): Promise<R[]> {
    const results: R[] = new Array(items.length) as any;
    const workerCount = Math.max(1, Math.min(concurrency, items.length || 1));
    let idx = 0;

    const workers = Array.from({ length: workerCount }, async () => {
      while (true) {
        const current = idx++;
        if (current >= items.length) break;
        results[current] = await mapper(items[current]);
      }
    });

    await Promise.all(workers);
    return results;
  }

  private matchesText(value: unknown, queryText?: string): boolean {
    if (!queryText) return true;
    const v = String(value ?? "").toLowerCase();
    const q = String(queryText).toLowerCase();
    return v.includes(q);
  }

  private async hydrateActiveSubscription(
    userId: string,
    activeSubscriptionId?: string
  ): Promise<UserSubscription | undefined> {
    if (!activeSubscriptionId) return undefined;

    try {
      const subscriptionDoc = await getDoc(doc(db, "users", userId, this.subscriptionGroupName, activeSubscriptionId));
      if (!subscriptionDoc.exists()) return undefined;
      return subscriptionDoc.data() as UserSubscription;
    } catch {
      return undefined;
    }
  }

  private subscriptionMatchesFilters(
    subscription: UserSubscription | undefined,
    filters?: { subscriptionStatus?: string; subscriptionProvider?: string }
  ): boolean {
    if (!filters) return true;
    if (filters.subscriptionStatus && subscription?.status !== filters.subscriptionStatus) return false;
    if (filters.subscriptionProvider && subscription?.provider !== filters.subscriptionProvider) return false;
    return true;
  }

  private encodeCursor(userId: string, subscriptionId: string, seenUserIds: Iterable<string>): string {
    return JSON.stringify({
      v: 2,
      userId,
      subscriptionId,
      seenUserIds: Array.from(seenUserIds),
    });
  }

  private decodeCursor(cursor?: string): SubscriptionUsersCursor | null {
    if (!cursor) return null;

    try {
      const decoded = JSON.parse(cursor);
      if (
        decoded?.v === 2 &&
        typeof decoded.userId === "string" &&
        typeof decoded.subscriptionId === "string"
      ) {
        return {
          userId: decoded.userId,
          subscriptionId: decoded.subscriptionId,
          seenUserIds: Array.isArray(decoded.seenUserIds)
            ? decoded.seenUserIds.filter((id: unknown): id is string => typeof id === "string" && id.length > 0)
            : [],
        };
      }
    } catch {
      // Fall through to the legacy cursor shape used by existing URLs.
    }

    const [userId, subscriptionId] = String(cursor).split("::");
    if (!userId || !subscriptionId) return null;
    return { userId, subscriptionId, seenUserIds: [userId] };
  }

  private async resolveCursorDoc(decoded: SubscriptionUsersCursor | null) {
    if (!decoded) return undefined;

    try {
      const cursorDoc = await getDoc(doc(db, "users", decoded.userId, this.subscriptionGroupName, decoded.subscriptionId));
      return cursorDoc.exists() ? cursorDoc : undefined;
    } catch {
      return undefined;
    }
  }

  private buildSubscriptionQuery(args: {
    status?: string;
    provider?: string;
    lastDoc?: any;
    pageSize: number;
  }) {
    const constraints: any[] = [];

    if (args.status) constraints.push(fsWhere("status", "==", args.status));
    if (args.provider) constraints.push(fsWhere("provider", "==", args.provider));

    constraints.push(orderBy("createdAt", "desc"), orderBy(documentId(), "desc"));

    if (args.lastDoc) constraints.push(startAfter(args.lastDoc));
    constraints.push(limit(args.pageSize));

    return query(collectionGroup(db, this.subscriptionGroupName), ...constraints);
  }

  private matchesLanguageCode(userData: any, languageCode?: string): boolean {
    if (!languageCode) return true;
    const normalized = String(languageCode).trim().toLowerCase();
    if (!normalized) return true;

    const learning = String(userData?.appMeta?.activeLearningLangCode ?? "").toLowerCase();

    return learning === normalized;
  }

  private makeListRow(
    userId: string,
    subscriptionId: string,
    subscription: UserSubscription,
    seenUserIds: Iterable<string>
  ) {
    return {
      id: userId,
      activeSubscriptionId: subscriptionId as any,
      activeSubscription: subscription,
      __cursor: this.encodeCursor(userId, subscriptionId, seenUserIds),
    } as SubscriptionUser & { id: string; __cursor: string };
  }

  async findSubscriptionUsers(args: {
    where?: any;
    offset?: string;
    limit?: number;
  }): Promise<(SubscriptionUser & { id: string })[]> {
    try {
      const pageSize = typeof args?.limit === "number" && args.limit > 0 ? args.limit : 10;
      const filters = args?.where || {};

      if (filters?.subscriptionStatus && filters.subscriptionStatus !== "ACTIVE") {
        return [];
      }

      const segmentSize = Math.max(50, pageSize * 10);
      const results: (SubscriptionUser & { id: string })[] = [];
      const cursor = this.decodeCursor(args?.offset);
      const seenUserIds = new Set<string>(cursor?.seenUserIds ?? []);
      const languageCode = filters?.languageCode ? String(filters.languageCode).trim().toLowerCase() : undefined;
      let lastSubDoc: any = await this.resolveCursorDoc(cursor);

      while (results.length < pageSize) {
        const subSnap = await getDocs(
          this.buildSubscriptionQuery({
            status: "ACTIVE",
            provider: filters?.subscriptionProvider,
            lastDoc: lastSubDoc,
            pageSize: segmentSize,
          })
        );

        if (subSnap.empty) break;
        lastSubDoc = subSnap.docs[subSnap.docs.length - 1];

        for (const subDoc of subSnap.docs) {
          const userId = subDoc.ref.parent.parent?.id;
          if (!userId || seenUserIds.has(userId)) continue;

          if (languageCode) {
            const userDoc = await getDoc(doc(this.collection, userId));
            if (!userDoc.exists()) continue;
            if (!this.matchesLanguageCode(userDoc.data(), languageCode)) continue;
          }

          seenUserIds.add(userId);
          results.push(this.makeListRow(userId, subDoc.id, subDoc.data() as UserSubscription, seenUserIds));

          if (results.length >= pageSize) break;
        }

        if (subSnap.size < segmentSize) break;
      }

      return results;
    } catch (error) {
      console.error("Error fetching subscription users:", error);
      throw error;
    }
  }

  async findById(id: string): Promise<SubscriptionUser & { id: string } | null> {
    try {
      const userDoc = await getDoc(doc(this.collection, id));

      if (!userDoc.exists()) {
        return null;
      }

      const userData = userDoc.data() as SubscriptionUser;
      const userWithId = { ...userData, id: userDoc.id };

      if (userData.activeSubscriptionId) {
        try {
          const subscriptionDoc = await getDoc(
            doc(db, "users", id, this.subscriptionGroupName, userData.activeSubscriptionId)
          );

          if (subscriptionDoc.exists()) {
            userWithId.activeSubscription = subscriptionDoc.data() as UserSubscription;
          }
        } catch (error) {
          console.warn(`Failed to fetch subscription for user ${id}:`, error);
        }
      }

      return userWithId;
    } catch (error) {
      console.error("Error fetching subscription user:", error);
      throw error;
    }
  }

  async getUserSubscriptions(userId: string): Promise<(UserSubscription & { id: string })[]> {
    try {
      const subscriptionsRef = collection(db, "users", userId, this.subscriptionGroupName);
      const q = query(subscriptionsRef, orderBy("createdAt", "desc"));
      const querySnapshot = await getDocs(q);

      return querySnapshot.docs.map((docSnapshot) => ({
        ...(docSnapshot.data() as UserSubscription),
        id: docSnapshot.id,
      }));
    } catch (error) {
      console.error("Error fetching user subscriptions:", error);
      throw error;
    }
  }

  async findAllUsersWithSubscriptions(args: {
    where?: any;
    offset?: string;
    limit?: number;
  }): Promise<(SubscriptionUser & { id: string })[]> {
    try {
      const pageSize = typeof args?.limit === "number" && args.limit > 0 ? args.limit : 10;
      const filters = args?.where || {};
      const segmentSize = Math.max(50, pageSize * 15);
      const results: (SubscriptionUser & { id: string })[] = [];
      const cursor = this.decodeCursor(args?.offset);
      const seenUserIds = new Set<string>(cursor?.seenUserIds ?? []);
      const languageCode = filters?.languageCode ? String(filters.languageCode).trim().toLowerCase() : undefined;
      let lastSubDoc: any = await this.resolveCursorDoc(cursor);

      while (results.length < pageSize) {
        const subSnap = await getDocs(
          this.buildSubscriptionQuery({
            status: filters?.subscriptionStatus,
            provider: filters?.subscriptionProvider,
            lastDoc: lastSubDoc,
            pageSize: segmentSize,
          })
        );

        if (subSnap.empty) break;
        lastSubDoc = subSnap.docs[subSnap.docs.length - 1];

        for (const subDoc of subSnap.docs) {
          const userId = subDoc.ref.parent.parent?.id;
          if (!userId || seenUserIds.has(userId)) continue;

          if (languageCode) {
            const userDoc = await getDoc(doc(this.collection, userId));
            if (!userDoc.exists()) continue;
            if (!this.matchesLanguageCode(userDoc.data(), languageCode)) continue;
          }

          seenUserIds.add(userId);
          results.push(this.makeListRow(userId, subDoc.id, subDoc.data() as UserSubscription, seenUserIds));

          if (results.length >= pageSize) break;
        }

        if (subSnap.size < segmentSize) break;
      }

      return results;
    } catch (error) {
      console.error("Error fetching users with previous subscriptions:", error);
      throw error;
    }
  }

  async count(where?: any): Promise<number> {
    try {
      const filters = where || {};
      if (filters?.subscriptionStatus && filters.subscriptionStatus !== "ACTIVE") {
        return 0;
      }
      const languageCode = filters?.languageCode ? String(filters.languageCode).trim().toLowerCase() : undefined;

      if (languageCode && !filters?.subscriptionProvider) {
        const usersCountSnapshot = await getCountFromServer(
          query(
            this.collection,
            fsWhere("appMeta.activeLearningLangCode", "==", languageCode),
            fsWhere("activeSubscriptionId", "!=", null),
            orderBy("activeSubscriptionId", "desc"),
            orderBy(documentId(), "desc")
          )
        );

        return usersCountSnapshot.data().count;
      }

      const constraints: any[] = [
        fsWhere("status", "==", "ACTIVE"),
        orderBy("createdAt", "desc"),
        orderBy(documentId(), "desc"),
      ];

      if (filters?.subscriptionProvider) {
        constraints.splice(1, 0, fsWhere("provider", "==", filters.subscriptionProvider));
      }

      const querySnapshot = await getDocs(query(collectionGroup(db, this.subscriptionGroupName), ...constraints));

      if (!languageCode) {
        const seenUserIds = new Set<string>();
        querySnapshot.docs.forEach((subDoc) => {
          const userId = subDoc.ref.parent.parent?.id;
          if (userId) seenUserIds.add(userId);
        });
        return seenUserIds.size;
      }

      const userIds = Array.from(
        new Set(querySnapshot.docs.map((subDoc) => subDoc.ref.parent.parent?.id).filter((userId): userId is string => !!userId))
      );

      const matches = await this.mapWithConcurrency(userIds, 20, async (userId) => {
        try {
          const userDoc = await getDoc(doc(this.collection, userId));
          return userDoc.exists() && this.matchesLanguageCode(userDoc.data(), languageCode);
        } catch {
          return false;
        }
      });

      return matches.filter(Boolean).length;
    } catch (error) {
      console.error("Error counting subscription users:", error);
      throw error;
    }
  }

  async countAllUsersWithSubscriptions(where?: any): Promise<number> {
    try {
      const filters = where || {};
      const languageCode = filters?.languageCode ? String(filters.languageCode).trim().toLowerCase() : undefined;
      const constraints: any[] = [];

      if (filters?.subscriptionStatus) constraints.push(fsWhere("status", "==", filters.subscriptionStatus));
      if (filters?.subscriptionProvider) constraints.push(fsWhere("provider", "==", filters.subscriptionProvider));

      const querySnapshot = await getDocs(query(collectionGroup(db, this.subscriptionGroupName), ...constraints));
      const seenUserIds = new Set<string>();

      for (const subDoc of querySnapshot.docs) {
        const userId = subDoc.ref.parent.parent?.id;
        if (!userId || seenUserIds.has(userId)) continue;
        seenUserIds.add(userId);
      }

      if (!languageCode) return seenUserIds.size;

      const matches = await this.mapWithConcurrency(Array.from(seenUserIds), 20, async (userId) => {
        try {
          const userDoc = await getDoc(doc(this.collection, userId));
          return userDoc.exists() && this.matchesLanguageCode(userDoc.data(), languageCode);
        } catch {
          return false;
        }
      });

      return matches.filter(Boolean).length;
    } catch (error) {
      console.error("Error counting users with previous subscriptions:", error);
      throw error;
    }
  }
}

export const subscriptionUserRepository = new SubscriptionUserRepository();
